import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminFlagStatusBadge,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  adminFormTextareaClasses,
  getAdminNoteAddedByDisplay,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import {
  INCIDENT_MODERATION_CASES_GRID,
  getDetailTablePagination,
} from '../../../utils/configurationTableUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import Profile from '../../../assets/img/User.png';

const SecurityIncidentDetail = () => {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);

  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({
    noteText: ''
  });

  // Row actions state
  const [customerRowActionsOpen, setCustomerRowActionsOpen] = useState({});
  const [businessRowActionsOpen, setBusinessRowActionsOpen] = useState({});
  const [deviceRowActionsOpen, setDeviceRowActionsOpen] = useState({});
  const [fraudCaseRowActionsOpen, setFraudCaseRowActionsOpen] = useState({});
  const [timelineRowActionsOpen, setTimelineRowActionsOpen] = useState({});
  const [linkedFraudCaseRowActionsOpen, setLinkedFraudCaseRowActionsOpen] = useState({});
  const [restrictionRowActionsOpen, setRestrictionRowActionsOpen] = useState({});
  const [paymentRowActionsOpen, setPaymentRowActionsOpen] = useState({});
  const [quoteRowActionsOpen, setQuoteRowActionsOpen] = useState({});
  const [moderationRowActionsOpen, setModerationRowActionsOpen] = useState({});
  const [moderationCasesPage, setModerationCasesPage] = useState(1);
  const [actionsRestrictionRowActionsOpen, setActionsRestrictionRowActionsOpen] = useState({});
  const customerRowActionsRefs = useRef({});
  const businessRowActionsRefs = useRef({});
  const deviceRowActionsRefs = useRef({});
  const fraudCaseRowActionsRefs = useRef({});
  const timelineRowActionsRefs = useRef({});
  const linkedFraudCaseRowActionsRefs = useRef({});
  const restrictionRowActionsRefs = useRef({});
  const paymentRowActionsRefs = useRef({});
  const quoteRowActionsRefs = useRef({});
  const moderationRowActionsRefs = useRef({});
  const actionsRestrictionRowActionsRefs = useRef({});

  // Toggle row actions
  const toggleCustomerRowActions = (customerId) => {
    setCustomerRowActionsOpen((prev) => {
      if (prev[customerId]) return {};
      return { [customerId]: true };
    });
  };

  const toggleBusinessRowActions = (businessId) => {
    setBusinessRowActionsOpen((prev) => {
      if (prev[businessId]) return {};
      return { [businessId]: true };
    });
  };

  const toggleDeviceRowActions = (deviceId) => {
    setDeviceRowActionsOpen((prev) => {
      if (prev[deviceId]) return {};
      return { [deviceId]: true };
    });
  };

  const toggleFraudCaseRowActions = (fraudCaseId) => {
    setFraudCaseRowActionsOpen((prev) => {
      if (prev[fraudCaseId]) return {};
      return { [fraudCaseId]: true };
    });
  };

  // Handle clicking outside to close row actions dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const allRefs = [
        ...Object.values(customerRowActionsRefs.current),
        ...Object.values(businessRowActionsRefs.current),
        ...Object.values(deviceRowActionsRefs.current),
        ...Object.values(fraudCaseRowActionsRefs.current),
        ...Object.values(timelineRowActionsRefs.current || {}),
        ...Object.values(linkedFraudCaseRowActionsRefs.current || {}),
        ...Object.values(restrictionRowActionsRefs.current || {}),
        ...Object.values(paymentRowActionsRefs.current || {}),
        ...Object.values(quoteRowActionsRefs.current || {}),
        ...Object.values(moderationRowActionsRefs.current || {}),
        ...Object.values(actionsRestrictionRowActionsRefs.current || {})
      ];
      
      const isOutsideAll = allRefs.every(ref => {
        const el = ref;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setCustomerRowActionsOpen({});
        setBusinessRowActionsOpen({});
        setDeviceRowActionsOpen({});
        setFraudCaseRowActionsOpen({});
        setTimelineRowActionsOpen({});
        setLinkedFraudCaseRowActionsOpen({});
        setRestrictionRowActionsOpen({});
        setPaymentRowActionsOpen({});
        setQuoteRowActionsOpen({});
        setModerationRowActionsOpen({});
        setActionsRestrictionRowActionsOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Notes handlers
  const openNotesModal = () => {
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNoteForm({ noteText: '' });
  };

  const handleNoteSave = () => {
    if (!noteForm.noteText || noteForm.noteText.trim() === '') {
      alert('Note is required');
      return;
    }

    const now = new Date();
    const timestamp = `${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    const date = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    
    // Add new note
    // In real app, get admin name and team from auth context
    const newNote = {
      id: `note-${Date.now()}`,
      createdAt: now.toISOString(),
      timestamp: timestamp,
      date: date,
      adminName: 'Admin — Mark Jones (Fraud & Security)', // In real app, get from auth
      author: 'Mark Jones',
      team: 'Fraud & Security',
      content: noteForm.noteText.trim(),
      noteContent: noteForm.noteText.trim()
    };
    
    setIncident(prev => ({
      ...prev,
      notes: [newNote, ...(prev.notes || [])]
    }));
    
    closeNotesModal();
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const dateLabel = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const timeLabel = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    return `${dateLabel} · ${timeLabel}`;
  };

  // Format datetime for Admin Notes display: "14 Jan 2026 · 10:42 AM"
  const formatNoteDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} · ${timePart}`;
  };

  // Format date only
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Severity badge color
  // Status badge color
  // Parse account/customer/business string to extract name (remove ID)
  const parseAccountName = (accountString) => {
    if (!accountString) return { name: '—', id: null };
    const parts = accountString.split(' · ');
    if (parts.length > 1) {
      return { name: parts[1], id: parts[0] };
    }
    return { name: accountString, id: null };
  };

  // Generate detailed incident data
  const generateIncidentDetail = (incidentIdParam, passedIncident) => {
    // Use passed incident if available, otherwise generate from incidentId
    const baseIncident = passedIncident || {
      id: incidentIdParam,
      type: 'Payment Fraud / Chargeback Chain',
      severity: 'Critical',
      status: 'Investigating',
      title: 'Chargeback ring across plumbing businesses',
      owner: 'David Lee (Security Lead)',
      involvesLegal: true,
      involvesLawEnforcement: false,
      source: 'Escalated from Fraud Alerts'
    };

    const now = new Date();
    const openedDate = baseIncident.openedDate ? new Date(baseIncident.openedDate) : new Date(now - 3 * 24 * 60 * 60 * 1000);
    const lastActivity = baseIncident.lastActivity ? new Date(baseIncident.lastActivity) : new Date(now - 0 * 24 * 60 * 60 * 1000);

    return {
      id: baseIncident.id || incidentIdParam,
      incidentId: baseIncident.id || incidentIdParam,
      type: baseIncident.type || 'Payment Fraud / Chargeback Chain',
      severity: baseIncident.severity || 'Critical',
      status: baseIncident.status || 'Investigating',
      title: baseIncident.title || 'Chargeback ring across plumbing businesses',
      owner: baseIncident.owner || 'David Lee (Security Lead)',
      backupOwner: 'Security & Fraud',
      openedDate: openedDate.toISOString(),
      lastActivity: lastActivity.toISOString(),
      lastUpdated: lastActivity.toISOString(),
      detectionSource: baseIncident.source || 'Escalated from Fraud Alerts (FR-000421, FR-000430)',
      involvesLegal: baseIncident.involvesLegal !== undefined ? baseIncident.involvesLegal : true,
      involvesLawEnforcement: baseIncident.involvesLawEnforcement !== undefined ? baseIncident.involvesLawEnforcement : false,
      source: baseIncident.source || 'Escalated from Fraud Alerts',
      
      // Short description
      shortDescription: 'Pattern of chargebacks and no-work-performed jobs affecting 4 plumbing businesses, multiple customers, and recurring Pro plan payments.',
      
      // Category tags
      categoryTags: ['Payment Fraud', 'Chargeback Ring', 'Organised Abuse'],
      
      // Time window
      timeWindow: {
        from: new Date(now - 36 * 24 * 60 * 60 * 1000).toISOString(),
        to: new Date(now - 0 * 24 * 60 * 60 * 1000).toISOString()
      },
      
      // Detected from
      detectedFrom: {
        fraudCases: ['FR-000421', 'FR-000430'],
        chargebacks: ['TX-88902', 'TX-88915', 'TX-88933']
      },
      
      // Quick stats
      quickStats: {
        affectedCustomers: 12,
        affectedBusinesses: 4,
        associatedFraudCases: 3,
        subscriptionChargebacks: 6,
        jobLevelChargebacks: 9
      },
      
      // Incident summary note
      incidentSummaryNote: 'Linked pattern: same few customers and devices initiating high-value quotes with certain plumbing businesses, demanding off-platform payments, then issuing chargebacks. Multiple Pro subscription payments tied to those businesses have also gone into chargeback. Potential organised abuse ring.',
      
      // Affected customers
      affectedCustomers: [
        {
          id: 'C-1043',
          name: 'John Smith',
          email: 'john.smith@example.com',
          mobile: '0400 123 456',
          status: 'Active',
          risk: 'High',
          linkedReason: 'Multiple chargebacks, review ring',
          activeRestrictions: 'None',
          notes: 'Initiated 3 high-value jobs',
          profilePicture: 'https://i.pravatar.cc/150?img=1'
        },
        {
          id: 'C-1177',
          name: 'Mark Jones',
          email: 'mark.jones@example.com',
          mobile: null,
          status: 'Restricted',
          risk: 'High',
          linkedReason: 'Scam messaging, quote abuse',
          activeRestrictions: 'Messaging Blocked (R-0101)',
          notes: 'Initiator in 2 chargeback jobs',
          profilePicture: 'https://i.pravatar.cc/150?img=2'
        }
      ],
      
      // Affected businesses
      affectedBusinesses: [
        {
          id: 'B-209',
          name: 'Melbourne Plumbing Co.',
          abn: '82 123 456 789',
          category: 'Plumbing',
          status: 'Active',
          verification: 'Verified',
          risk: 'High',
          linkedReason: 'Repeated chargebacks, high-risk quote patterns',
          activeRestrictions: 'Posting Blocked (R-0102)',
          profilePicture: 'https://i.pravatar.cc/150?img=3'
        },
        {
          id: 'B-256',
          name: 'Citywide Hot Water',
          abn: '11 222 333 444',
          category: 'Plumbing',
          status: 'Active',
          verification: 'Verified',
          risk: 'Medium',
          linkedReason: 'Linked to 2 high-risk complaints',
          activeRestrictions: 'None',
          profilePicture: 'https://i.pravatar.cc/150?img=4'
        }
      ],
      
      // Devices / IPs
      devices: [
        {
          deviceId: 'Device-9A2',
          type: 'Mobile',
          linkedAccounts: 'C-1043, C-1177, C-1201',
          notes: 'Used across multiple incidents'
        },
        {
          deviceId: 'Device-7F9',
          type: 'Desktop',
          linkedAccounts: 'B-209 staff, C-1300',
          notes: 'Shared between business & customer'
        }
      ],
      
      ipAddresses: [
        {
          ip: '203.0.113.45',
          geo: 'Melbourne, AU',
          linkedAccounts: '8 customer accounts',
          notes: 'High concentration'
        },
        {
          ip: '198.51.100.10',
          geo: 'VPN Hosting Provider',
          linkedAccounts: '5 business logins',
          notes: 'Suspicious provider usage'
        }
      ],
      
      // Related fraud cases
      relatedFraudCases: [
        {
          id: 'FR-000421',
          alertType: 'Multiple Accounts – Same Device',
          risk: 'High',
          status: 'Under Review',
          primaryEntity: 'Customer C-1043 · John Smith'
        },
        {
          id: 'FR-000430',
          alertType: 'Payment Chargeback Chain',
          risk: 'High',
          status: 'Actioned',
          primaryEntity: 'Business B-209 · Melbourne Plumbing Co.'
        },
        {
          id: 'FR-000440',
          alertType: 'Review Ring / Reputation Abuse',
          risk: 'Medium',
          status: 'Under Review',
          primaryEntity: 'Customers (3) across 2 businesses'
        }
      ],
      
      // Incident timeline
      timeline: [
        {
          timestamp: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
          actor: 'System',
          actorType: 'System',
          eventType: 'Incident Created',
          event: 'Security Incident opened from Fraud Cases FR-000421, FR-000430',
          target: 'INC-0012',
          result: 'Status: Open',
          source: 'Fraud Alerts'
        },
        {
          timestamp: new Date(now - 3 * 24 * 60 * 60 * 1000 + 85 * 60 * 1000).toISOString(),
          actor: 'David Lee',
          actorType: 'Admin',
          eventType: 'Severity Set',
          event: 'Severity set to Critical',
          target: 'INC-0012',
          result: 'Medium → Critical',
          source: 'Security'
        },
        {
          timestamp: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
          actor: 'System',
          actorType: 'System',
          eventType: 'Payment Event',
          event: 'New chargeback received',
          target: 'TX-88933',
          result: 'Paid → Chargeback',
          source: 'Payments'
        },
        {
          timestamp: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
          actor: 'System',
          actorType: 'System',
          eventType: 'Restriction Applied',
          event: 'Posting Blocked',
          target: 'B-209',
          result: 'New Restriction R-0102',
          source: 'Restrictions'
        },
        {
          timestamp: new Date(now - 0 * 24 * 60 * 60 * 1000 - 10 * 60 * 60 * 1000).toISOString(),
          actor: 'Sarah Nguyen',
          actorType: 'Admin',
          eventType: 'Incident Update',
          event: 'Affected accounts list updated',
          target: 'INC-0012',
          result: 'Affected Customers: 10 → 12',
          source: 'Security'
        },
        {
          timestamp: new Date(now - 0 * 24 * 60 * 60 * 1000 - 7 * 60 * 60 * 1000).toISOString(),
          actor: 'David Lee',
          actorType: 'Admin',
          eventType: 'Legal Notified',
          event: 'Legal team notified and initial review logged',
          target: 'INC-0012',
          result: 'Legal Involvement: No → Yes',
          source: 'Security'
        }
      ],
      
      // Linked data
      linkedData: {
        fraudCases: [
          {
            id: 'FR-000421',
            alertType: 'Multiple Accounts – Same Device',
            risk: 'High',
            status: 'Under Review',
            primaryEntity: 'Customer C-1043'
          },
          {
            id: 'FR-000430',
            alertType: 'Payment Chargeback Chain',
            risk: 'High',
            status: 'Actioned',
            primaryEntity: 'Business B-209'
          }
        ],
        restrictions: [
          {
            id: 'R-0101',
            account: 'C-1177 · Mark Jones',
            accountType: 'Customer',
            restrictionType: 'Messaging Blocked',
            status: 'Active',
            reason: 'Messaging scam'
          },
          {
            id: 'R-0102',
            account: 'B-209 · Melbourne Plumbing Co.',
            accountType: 'Business',
            restrictionType: 'Posting Blocked',
            status: 'Active',
            reason: 'Chargeback risk'
          }
        ],
        payments: [
          {
            txId: 'TX-88902',
            business: 'B-209 · Melbourne Plumbing Co.',
            plan: 'Pro',
            amount: 149,
            paymentStatus: 'Chargeback',
            chargebackStatus: 'Open'
          },
          {
            txId: 'TX-88915',
            business: 'B-209 · Melbourne Plumbing Co.',
            plan: 'Pro',
            amount: 149,
            paymentStatus: 'Chargeback',
            chargebackStatus: 'Lost'
          }
        ],
        quotes: [
          {
            id: 'Q-2201',
            customer: 'C-1043 · John Smith',
            business: 'B-209 · Melbourne Plumbing Co.',
            jobStatus: 'Completed',
            chargebackComplaint: 'Chargeback Reported',
            amount: 2100
          }
        ],
        moderationCases: [
          {
            id: 'MC-00421',
            type: 'Moderation Case',
            target: 'Reviews (4 linked)',
            severity: 'High',
            status: 'Under Review'
          }
        ]
      },
      
      // Impact & Escalation
      impact: {
        accountsAffected: {
          customers: 12,
          businesses: 4
        },
        financialExposure: 38500,
        confirmedLoss: 16200,
        regionsAffected: ['VIC', 'NSW'],
        coreAreas: ['Plumbing category', 'Pro subscriptions', 'High-value jobs']
      },
      
      legal: {
        legalTeamInvolved: true,
        legalOwner: 'Jane Kim (Legal Counsel)',
        legalNotes: 'Initial review completed, monitoring for escalation',
        regulators: ['ACCC', 'Fair Trading (VIC)'],
        dataBreachPrivacyImpact: 'None'
      },
      
      lawEnforcement: {
        notified: false,
        agency: null,
        referenceNumber: null,
        date: null,
        thirdPartyPlatforms: ['Banks / Payment providers', 'Card networks']
      },
      
      // Actions & Restrictions
      actionsRestrictions: {
        activeRestrictions: 5,
        accountsFullySuspended: 2,
        permanentBans: 1,
        postingBlocked: 3,
        restrictions: [
          {
            id: 'R-0101',
            account: 'C-1177 · Mark Jones',
            accountType: 'Customer',
            restrictionType: 'Messaging Blocked',
            reason: 'Messaging scam',
            startDate: new Date(now - 0 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'Active'
          },
          {
            id: 'R-0102',
            account: 'B-209 · Melbourne Plumbing Co.',
            accountType: 'Business',
            restrictionType: 'Posting Blocked',
            reason: 'Chargeback risk',
            startDate: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'Active'
          },
          {
            id: 'R-0105',
            account: 'B-256 · Citywide Hot Water',
            accountType: 'Business',
            restrictionType: 'Temporary Suspension',
            reason: 'High-risk fraud pattern',
            startDate: new Date(now - 0 * 24 * 60 * 60 * 1000 - 12 * 60 * 60 * 1000).toISOString(),
            status: 'Active'
          }
        ],
        recommendedActions: [
          'Consider permanent ban for C-1177 if one more confirmed scam.',
          'Review whether to restore posting for B-256 after 7 days if no new events.',
          'Escalate to Legal if confirmed financial loss exceeds $50,000.'
        ]
      },
      
      // Notes
      notes: [
        {
          id: 'N-INC-01',
          createdAt: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
          timestamp: `${new Date(now - 2 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${new Date(now - 2 * 24 * 60 * 60 * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
          date: new Date(now - 2 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
          adminName: 'Admin — David Lee',
          author: 'David Lee',
          category: 'Risk / Fraud',
          content: 'Cluster appears coordinated; same device, IP, and payment patterns across multiple accounts. Recommend deeper investigation into device sharing.',
          noteContent: 'Cluster appears coordinated; same device, IP, and payment patterns across multiple accounts. Recommend deeper investigation into device sharing.'
        },
        {
          id: 'N-INC-02',
          createdAt: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
          timestamp: `${new Date(now - 1 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${new Date(now - 1 * 24 * 60 * 60 * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
          date: new Date(now - 1 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
          adminName: 'Admin — Jane Kim',
          author: 'Jane Kim',
          category: 'Compliance',
          content: 'Possible exposure under Fair Trading if we fail to respond promptly. Recommend proactive communication with affected businesses.',
          noteContent: 'Possible exposure under Fair Trading if we fail to respond promptly. Recommend proactive communication with affected businesses.'
        }
      ]
    };
  };

  // Load incident detail
  useEffect(() => {
    const fetchIncidentDetail = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check if incident data was passed via navigation state
        let passedIncident = location.state?.incident;
        
        // Generate incident detail data
        const incidentData = generateIncidentDetail(incidentId, passedIncident);
        
        // Ensure we have valid incident data
        if (incidentData && incidentData.id) {
          setIncident(incidentData);
        } else {
          console.error('Failed to generate incident data');
          setIncident(null);
        }
      } catch (err) {
        console.error('Failed to load incident:', err);
        setIncident(null);
      } finally {
        setLoading(false);
      }
    };

    fetchIncidentDetail();
  }, [incidentId, location.state]);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'affectedEntities', label: 'Affected Entities' },
    { id: 'timeline', label: 'Incident Timeline' },
    { id: 'linkedData', label: 'Linked Data' },
    { id: 'impact', label: 'Impact & Escalation' },
    { id: 'actions', label: 'Actions & Restrictions' },
    { id: 'notes', label: 'Admin Notes' }
  ];

  const incidentModerationCases = useMemo(
    () => incident?.linkedData?.moderationCases || [],
    [incident]
  );

  useEffect(() => {
    setModerationCasesPage(1);
  }, [incidentId, activeTab]);

  const moderationCasesPagination = getDetailTablePagination(
    incidentModerationCases.length,
    moderationCasesPage
  );
  const paginatedModerationCases = moderationCasesPagination.paginate(incidentModerationCases);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading incident details...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!incident) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-alertred mb-4">Security incident not found</p>
            <AdminLinkAction type="button" onClick={() => navigate('/admin/fraud/incidents')}>
              Back to security incidents
            </AdminLinkAction>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>

          {/* Minimal Header — avatar + title only */}
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src={Profile}
                  alt={`${incident.id} ${incident.type}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = Profile;
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <AdminPanelTitle className="text-offblack">
                  {incident.type} — {incident.title}
                </AdminPanelTitle>
              </div>
            </div>
          </DashboardMainContainer>

          {/* Tabs */}
          <DashboardMainContainer className="!bg-white">
            <div className="border-b border-lightgray">
              <div className="flex space-x-8 overflow-x-auto">
                {tabs.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className={adminDetailTabContentClassName(activeTab)}>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Overview Tab - 4 Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Column 1: Incident Summary */}
                <DashboardMainContainer className="!bg-white">
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Incident ID</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.id}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Title</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.title}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Type</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.type}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Severity</AdminDetailFieldLabel>
                      <div>
                        <AdminStatusBadge status={incident.severity} label={incident.severity} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                      <div>
                        <AdminStatusBadge status={incident.status} label={incident.status} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Detection Source</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.detectionSource}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Opened</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{formatDateTime(incident.openedDate)}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Last Activity</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{formatDateTime(incident.lastActivity)}</AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Last Updated</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{formatDateTime(incident.lastUpdated)}</div>
                    </div>
                  </div>
                </DashboardMainContainer>

                {/* Column 2: Details & Scope */}
                <DashboardMainContainer className="!bg-white">
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Short Description</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.shortDescription}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Incident Category Tags</AdminDetailFieldLabel>
                      <div className="flex flex-wrap gap-2">
                        {incident.categoryTags.map((tag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-lightgray text-offblack rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Time Window</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {formatDate(incident.timeWindow.from)} – {formatDate(incident.timeWindow.to)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Detected From</AdminDetailFieldLabel>
                      <div className="space-y-1">
                        <div className="text-sm text-offblack font-medium">
                          Fraud Cases: {incident.detectedFrom.fraudCases.join(', ')}
                        </div>
                        <div className="text-sm text-offblack font-medium">
                          Chargebacks: {incident.detectedFrom.chargebacks.join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                </DashboardMainContainer>

                {/* Column 3: Severity & Ownership */}
                <DashboardMainContainer className="!bg-white">
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Severity</AdminDetailFieldLabel>
                      <div>
                        <AdminStatusBadge status={incident.severity} label={incident.severity} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Owner</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.owner}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Backup Owner / Team</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.backupOwner}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Involves Legal?</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.involvesLegal ? 'Yes' : 'No'}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Involves Law Enforcement?</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{incident.involvesLawEnforcement ? 'Yes' : 'No'}</div>
                    </div>
                  </div>
                </DashboardMainContainer>

                {/* Column 4: Quick Stats */}
                <DashboardMainContainer className="!bg-white">
                  <div className="space-y-4">
                    <button
                      onClick={() => setActiveTab('affectedEntities')}
                      className="text-left w-full hover:text-primaryblue transition-colors space-y-1"
                    >
                      <AdminDetailFieldLabel>Affected Customers</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.quickStats.affectedCustomers}
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('affectedEntities')}
                      className="text-left w-full hover:text-primaryblue transition-colors space-y-1"
                    >
                      <AdminDetailFieldLabel>Affected Businesses</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.quickStats.affectedBusinesses}
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('linkedData')}
                      className="text-left w-full hover:text-primaryblue transition-colors space-y-1"
                    >
                      <AdminDetailFieldLabel>Associated Fraud Cases</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.quickStats.associatedFraudCases}
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('linkedData')}
                      className="text-left w-full hover:text-primaryblue transition-colors space-y-1"
                    >
                      <AdminDetailFieldLabel>Subscription Chargebacks</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.quickStats.subscriptionChargebacks}
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('linkedData')}
                      className="text-left w-full hover:text-primaryblue transition-colors space-y-1"
                    >
                      <AdminDetailFieldLabel>Job-level Chargebacks</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.quickStats.jobLevelChargebacks}
                      </div>
                    </button>
                  </div>
                </DashboardMainContainer>
              </div>

              {/* Short Incident Summary Note - Full Width */}
              <DashboardMainContainer className="!bg-white">
                <div className="space-y-2">
                  <AdminPanelTitle className="text-offblack">
                    Short Incident Summary Note
                  </AdminPanelTitle>
                  <AdminMainText className="text-offblack">
                    {incident.incidentSummaryNote}
                  </AdminMainText>
                </div>
              </DashboardMainContainer>

              {/* Quick Actions - Full Width */}
              <DashboardMainContainer className="!bg-white">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                      <AdminHeaderAction
                      onClick={() => {
                        const newSeverity = window.prompt('Change Severity (Critical / High / Medium):', incident.severity);
                        if (newSeverity && ['Critical', 'High', 'Medium'].includes(newSeverity)) {
                          console.log('Change severity:', incident.id, 'to', newSeverity);
                        }
                      }}
                    >
                      Change Severity
                    </AdminHeaderAction>
                      <AdminHeaderAction
                      onClick={() => {
                        const newStatus = window.prompt('Change Status (Open / Investigating / Mitigated / Closed):', incident.status);
                        if (newStatus && ['Open', 'Investigating', 'Mitigated', 'Closed'].includes(newStatus)) {
                          console.log('Change status:', incident.id, 'to', newStatus);
                        }
                      }}
                    >
                      Change Status
                    </AdminHeaderAction>
                      <AdminHeaderAction
                      onClick={() => {
                        const newOwner = window.prompt('Assign / Reassign Owner:', incident.owner);
                        if (newOwner) {
                          console.log('Assign owner:', incident.id, 'to', newOwner);
                        }
                      }}
                    >
                      Assign / Reassign Owner
                    </AdminHeaderAction>
                      <AdminHeaderAction
                      onClick={() => {
                        setActiveTab('linkedData');
                      }}
                    >
                      View All Related Fraud Cases
                    </AdminHeaderAction>
                      <AdminHeaderAction
                      onClick={() => {
                        navigate('/admin/fraud/restrictions?incidentId=' + incident.id);
                      }}
                    >
                      View All Restrictions
                    </AdminHeaderAction>
                  </div>
                </div>
              </DashboardMainContainer>
            </div>
          )}

          {/* Affected Entities Tab */}
          {activeTab === 'affectedEntities' && (
            <div className="space-y-6">
              {/* Affected Customers Table */}
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Affected Customers
                  </AdminPanelTitle>
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(150px, 2fr) minmax(150px, 2fr) minmax(100px, 1.2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) auto' }}>
                    <div className="flex items-center"><span className="font-semibold">Customer</span></div>
                    <div className="flex items-center"><span className="font-semibold">Email</span></div>
                    <div className="flex items-center"><span className="font-semibold">Mobile</span></div>
                    <div className="flex items-center"><span className="font-semibold">Status</span></div>
                    <div className="flex items-center"><span className="font-semibold">Risk</span></div>
                    <div className="flex items-center"><span className="font-semibold">Linked Reason</span></div>
                    <div className="flex items-center"><span className="font-semibold">Active Restrictions</span></div>
                    <div className="flex items-center"><span className="font-semibold">Notes</span></div>
                    <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                  </div>
                  {incident.affectedCustomers.map((customer, idx) => (
                    <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(150px, 2fr) minmax(150px, 2fr) minmax(100px, 1.2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) auto' }}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                          <img 
                            src={customer.profilePicture || Profile}
                            alt={customer.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = Profile;
                            }}
                          />
                        </div>
                        <span className="" title={customer.name}>
                          {customer.name}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="">{customer.email}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="">{customer.mobile || 'Not Provided'}</span>
                      </div>
                      <div className="flex items-center">
                        <AdminStatusBadge status={customer.status} label={customer.status} />
                      </div>
                      <div className="flex items-center">
                        <AdminStatusBadge status={customer.risk} label={customer.risk} />
                      </div>
                      <div className="flex items-center">
                        <span className="">{customer.linkedReason}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="">{customer.activeRestrictions}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="">{customer.notes}</span>
                      </div>
                      <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                        <div className="relative inline-flex" ref={el => customerRowActionsRefs.current[customer.id] = el}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCustomerRowActions(customer.id);
                            }}
                            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                            title="Row Actions"
                          >
                            <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                          </button>
                          {customerRowActionsOpen[customer.id] && (
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                              <div className="py-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/users/customers/${customer.id}`);
                                    setCustomerRowActionsOpen(prev => ({ ...prev, [customer.id]: false }));
                                  }}
                                  className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                >
                                  <AdminSupportingText>Open Customer Profile</AdminSupportingText>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </DashboardMainContainer>

              {/* Affected Businesses Table */}
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Affected Businesses
                  </AdminPanelTitle>
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(150px, 2fr) minmax(120px, 1.5fr) minmax(100px, 1.2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(150px, 1.5fr) auto' }}>
                    <div className="flex items-center"><span className="font-semibold">Business</span></div>
                    <div className="flex items-center"><span className="font-semibold">ABN</span></div>
                    <div className="flex items-center"><span className="font-semibold">Category</span></div>
                    <div className="flex items-center"><span className="font-semibold">Status</span></div>
                    <div className="flex items-center"><span className="font-semibold">Verification</span></div>
                    <div className="flex items-center"><span className="font-semibold">Risk</span></div>
                    <div className="flex items-center"><span className="font-semibold">Linked Reason</span></div>
                    <div className="flex items-center"><span className="font-semibold">Active Restrictions</span></div>
                    <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                  </div>
                  {incident.affectedBusinesses.map((business, idx) => (
                    <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(150px, 2fr) minmax(120px, 1.5fr) minmax(100px, 1.2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(150px, 1.5fr) auto' }}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                          <img 
                            src={business.profilePicture || Profile}
                            alt={business.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = Profile;
                            }}
                          />
                        </div>
                        <span className="" title={business.name}>
                          {business.name}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="">{business.abn}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="">{business.category}</span>
                      </div>
                      <div className="flex items-center">
                        <AdminStatusBadge status={business.status} label={business.status} />
                      </div>
                      <div className="flex items-center">
                        <span className="">{business.verification}</span>
                      </div>
                      <div className="flex items-center">
                        <AdminStatusBadge status={business.risk} label={business.risk} />
                      </div>
                      <div className="flex items-center">
                        <span className="">{business.linkedReason}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="">{business.activeRestrictions}</span>
                      </div>
                      <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                        <div className="relative inline-flex" ref={el => businessRowActionsRefs.current[business.id] = el}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBusinessRowActions(business.id);
                            }}
                            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                            title="Row Actions"
                          >
                            <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                          </button>
                          {businessRowActionsOpen[business.id] && (
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                              <div className="py-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/users/businesses/${business.id}`);
                                    setBusinessRowActionsOpen(prev => ({ ...prev, [business.id]: false }));
                                  }}
                                  className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                >
                                  <AdminSupportingText>Open Business Profile</AdminSupportingText>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </DashboardMainContainer>

              {/* Devices / IPs */}
              <DashboardMainContainer className="!bg-white">
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Devices / IPs
                  </AdminPanelTitle>
                  <div className="space-y-6">
                    {/* Devices Table */}
                    <div>
                      <AdminSupportingText>Devices</AdminSupportingText>
                      <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.5fr) minmax(100px, 1fr) minmax(200px, 2fr) minmax(150px, 1.5fr) auto' }}>
                        <div className="flex items-center"><span className="font-semibold">Device ID</span></div>
                        <div className="flex items-center"><span className="font-semibold">Type</span></div>
                        <div className="flex items-center"><span className="font-semibold">Linked Accounts</span></div>
                        <div className="flex items-center"><span className="font-semibold">Notes</span></div>
                        <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                      </div>
                      {incident.devices.map((device, idx) => (
                        <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.5fr) minmax(100px, 1fr) minmax(200px, 2fr) minmax(150px, 1.5fr) auto' }}>
                          <div className="flex items-center"><span className="">{device.deviceId}</span></div>
                          <div className="flex items-center"><span className="">{device.type}</span></div>
                          <div className="flex items-center"><span className="">{device.linkedAccounts}</span></div>
                          <div className="flex items-center"><span className="">{device.notes}</span></div>
                          <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                            <div className="relative inline-flex" ref={el => deviceRowActionsRefs.current[device.deviceId] = el}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDeviceRowActions(device.deviceId);
                                }}
                                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                                title="Row Actions"
                              >
                                <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                              </button>
                              {deviceRowActionsOpen[device.deviceId] && (
                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                                  <div className="py-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // TODO: Navigate to device view
                                        setDeviceRowActionsOpen(prev => ({ ...prev, [device.deviceId]: false }));
                                      }}
                                      className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                    >
                                      <AdminSupportingText>View Device</AdminSupportingText>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* IP Addresses Table */}
                    <div>
                      <AdminSupportingText>IP Addresses</AdminSupportingText>
                      <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(150px, 1.5fr) minmax(150px, 1.5fr) minmax(200px, 2fr) minmax(150px, 1.5fr)' }}>
                        <div className="flex items-center"><span className="font-semibold">IP Address</span></div>
                        <div className="flex items-center"><span className="font-semibold">Geo</span></div>
                        <div className="flex items-center"><span className="font-semibold">Linked Accounts</span></div>
                        <div className="flex items-center"><span className="font-semibold">Notes</span></div>
                      </div>
                      {incident.ipAddresses.map((ip, idx) => (
                        <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(150px, 1.5fr) minmax(150px, 1.5fr) minmax(200px, 2fr) minmax(150px, 1.5fr)' }}>
                          <div className="flex items-center"><span className="">{ip.ip}</span></div>
                          <div className="flex items-center"><span className="">{ip.geo}</span></div>
                          <div className="flex items-center"><span className="">{ip.linkedAccounts}</span></div>
                          <div className="flex items-center"><span className="">{ip.notes}</span></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </DashboardMainContainer>

              {/* Related Fraud Cases Table */}
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Related Fraud Cases
                  </AdminPanelTitle>
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(200px, 2fr) auto' }}>
                    <div className="flex items-center"><span className="font-semibold">Case ID</span></div>
                    <div className="flex items-center"><span className="font-semibold">Alert Type</span></div>
                    <div className="flex items-center"><span className="font-semibold">Risk</span></div>
                    <div className="flex items-center"><span className="font-semibold">Status</span></div>
                    <div className="flex items-center"><span className="font-semibold">Primary Entity</span></div>
                    <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                  </div>
                  {incident.relatedFraudCases.map((fraudCase, idx) => (
                    <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(200px, 2fr) auto' }}>
                      <div className="flex items-center"><span className="">{fraudCase.id}</span></div>
                      <div className="flex items-center"><span className="">{fraudCase.alertType}</span></div>
                      <div className="flex items-center">
                        <AdminStatusBadge status={fraudCase.risk} label={fraudCase.risk} />
                      </div>
                      <div className="flex items-center">
                        <AdminStatusBadge status={fraudCase.status} label={fraudCase.status} />
                      </div>
                      <div className="flex items-center"><span className="">{fraudCase.primaryEntity}</span></div>
                      <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                        <div className="relative inline-flex" ref={el => fraudCaseRowActionsRefs.current[fraudCase.id] = el}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFraudCaseRowActions(fraudCase.id);
                            }}
                            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                            title="Row Actions"
                          >
                            <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                          </button>
                          {fraudCaseRowActionsOpen[fraudCase.id] && (
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                              <div className="py-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/fraud/alerts/${fraudCase.id}`);
                                    setFraudCaseRowActionsOpen(prev => ({ ...prev, [fraudCase.id]: false }));
                                  }}
                                  className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                >
                                  <AdminSupportingText>Open Fraud Case</AdminSupportingText>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </DashboardMainContainer>
            </div>
          )}

          {/* Incident Timeline Tab */}
          {activeTab === 'timeline' && (
            <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
              <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(140px, 1.2fr) minmax(100px, 1fr) minmax(90px, 0.9fr) minmax(130px, 1.1fr) minmax(180px, 1.8fr) minmax(100px, 1fr) minmax(130px, 1.1fr) minmax(100px, 1fr) 60px' }}>
                <div className="flex items-center"><span className="font-semibold">Timestamp</span></div>
                <div className="flex items-center"><span className="font-semibold">Actor</span></div>
                <div className="flex items-center"><span className="font-semibold">Actor Type</span></div>
                <div className="flex items-center"><span className="font-semibold">Event Type</span></div>
                <div className="flex items-center"><span className="font-semibold">Event</span></div>
                <div className="flex items-center"><span className="font-semibold">Target</span></div>
                <div className="flex items-center"><span className="font-semibold">Result</span></div>
                <div className="flex items-center"><span className="font-semibold">Source</span></div>
                <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
              </div>
              {incident.timeline.map((event, idx) => (
                <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(140px, 1.2fr) minmax(100px, 1fr) minmax(90px, 0.9fr) minmax(130px, 1.1fr) minmax(180px, 1.8fr) minmax(100px, 1fr) minmax(130px, 1.1fr) minmax(100px, 1fr) 60px' }}>
                  <div className="flex items-center"><span className="">{formatDateTime(event.timestamp)}</span></div>
                  <div className="flex items-center"><span className="">{event.actor}</span></div>
                  <div className="flex items-center"><span className="">{event.actorType}</span></div>
                  <div className="flex items-center"><span className="">{event.eventType}</span></div>
                  <div className="flex items-center"><span className="">{event.event}</span></div>
                  <div className="flex items-center"><span className="">{event.target}</span></div>
                  <div className="flex items-center"><span className="">{event.result}</span></div>
                  <div className="flex items-center"><span className="">{event.source}</span></div>
                  <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                    <div className="relative inline-flex" ref={el => timelineRowActionsRefs.current[`timeline-${idx}`] = el}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const eventId = `timeline-${idx}`;
                          setTimelineRowActionsOpen((prev) =>
                            prev[eventId] ? {} : { [eventId]: true }
                          );
                        }}
                        className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                        title="Row Actions"
                      >
                        <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                      </button>
                      {timelineRowActionsOpen[`timeline-${idx}`] && (
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                          <div className="py-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // TODO: Navigate to event detail view
                                setTimelineRowActionsOpen(prev => ({ ...prev, [`timeline-${idx}`]: false }));
                              }}
                              className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                            >
                              <AdminSupportingText>View Event</AdminSupportingText>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </DashboardMainContainer>
          )}

          {/* Linked Data Tab */}
          {activeTab === 'linkedData' && (
            <div className="space-y-6">
              {/* Fraud Cases */}
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Fraud Cases
                  </AdminPanelTitle>
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(180px, 2fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(150px, 1.5fr) 60px' }}>
                    <div className="flex items-center"><span className="font-semibold">Case ID</span></div>
                    <div className="flex items-center"><span className="font-semibold">Alert Type</span></div>
                    <div className="flex items-center"><span className="font-semibold">Risk</span></div>
                    <div className="flex items-center"><span className="font-semibold">Status</span></div>
                    <div className="flex items-center"><span className="font-semibold">Primary Entity</span></div>
                    <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                  </div>
                  {incident.linkedData.fraudCases.map((fraudCase, idx) => (
                    <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(180px, 2fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(150px, 1.5fr) 60px' }}>
                      <div className="flex items-center"><span className="">{fraudCase.id}</span></div>
                      <div className="flex items-center"><span className="">{fraudCase.alertType}</span></div>
                      <div className="flex items-center">
                        <AdminStatusBadge status={fraudCase.risk} label={fraudCase.risk} />
                      </div>
                      <div className="flex items-center">
                        <AdminStatusBadge status={fraudCase.status} label={fraudCase.status} />
                      </div>
                      <div className="flex items-center"><span className="">{fraudCase.primaryEntity}</span></div>
                      <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                        <div className="relative inline-flex" ref={el => linkedFraudCaseRowActionsRefs.current[fraudCase.id] = el}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLinkedFraudCaseRowActionsOpen((prev) =>
                                prev[fraudCase.id] ? {} : { [fraudCase.id]: true }
                              );
                            }}
                            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                            title="Row Actions"
                          >
                            <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                          </button>
                          {linkedFraudCaseRowActionsOpen[fraudCase.id] && (
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                              <div className="py-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/fraud/alerts/${fraudCase.id}`);
                                    setLinkedFraudCaseRowActionsOpen(prev => ({ ...prev, [fraudCase.id]: false }));
                                  }}
                                  className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                >
                                  <AdminSupportingText>Open Fraud Case</AdminSupportingText>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </DashboardMainContainer>

              {/* Account Restrictions */}
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Account Restrictions
                  </AdminPanelTitle>
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 2fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(150px, 1.5fr) 60px' }}>
                    <div className="flex items-center"><span className="font-semibold">Restriction ID</span></div>
                    <div className="flex items-center"><span className="font-semibold">Account</span></div>
                    <div className="flex items-center"><span className="font-semibold">Type</span></div>
                    <div className="flex items-center"><span className="font-semibold">Restriction Type</span></div>
                    <div className="flex items-center"><span className="font-semibold">Status</span></div>
                    <div className="flex items-center"><span className="font-semibold">Reason</span></div>
                    <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                  </div>
                  {incident.linkedData.restrictions.map((restriction, idx) => {
                    const accountInfo = parseAccountName(restriction.account);
                    return (
                      <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 2fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(150px, 1.5fr) 60px' }}>
                        <div className="flex items-center"><span className="">{restriction.id}</span></div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                            <img 
                              src={Profile}
                              alt={accountInfo.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="" title={accountInfo.name}>
                            {accountInfo.name}
                          </span>
                        </div>
                        <div className="flex items-center"><span className="">{restriction.accountType}</span></div>
                        <div className="flex items-center"><span className="">{restriction.restrictionType}</span></div>
                        <div className="flex items-center">
                          <AdminStatusBadge status={restriction.status} label={restriction.status} />
                        </div>
                        <div className="flex items-center"><span className="">{restriction.reason}</span></div>
                        <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                          <div className="relative inline-flex" ref={el => restrictionRowActionsRefs.current[restriction.id] = el}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRestrictionRowActionsOpen((prev) =>
                                  prev[restriction.id] ? {} : { [restriction.id]: true }
                                );
                              }}
                              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                              title="Row Actions"
                            >
                              <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                            </button>
                            {restrictionRowActionsOpen[restriction.id] && (
                              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                                <div className="py-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/admin/fraud/restrictions/${restriction.id}`);
                                      setRestrictionRowActionsOpen(prev => ({ ...prev, [restriction.id]: false }));
                                    }}
                                    className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                  >
                                    <AdminSupportingText>Open Restriction</AdminSupportingText>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DashboardMainContainer>

              {/* Payments & Chargebacks */}
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Payments & Chargebacks (Business Subscriptions)
                  </AdminPanelTitle>
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 2fr) minmax(80px, 1fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(130px, 1.3fr) 60px' }}>
                    <div className="flex items-center"><span className="font-semibold">TX ID</span></div>
                    <div className="flex items-center"><span className="font-semibold">Business</span></div>
                    <div className="flex items-center"><span className="font-semibold">Plan</span></div>
                    <div className="flex items-center"><span className="font-semibold">Amount</span></div>
                    <div className="flex items-center"><span className="font-semibold">Payment Status</span></div>
                    <div className="flex items-center"><span className="font-semibold">Chargeback Status</span></div>
                    <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                  </div>
                  {incident.linkedData.payments.map((payment, idx) => {
                    const businessInfo = parseAccountName(payment.business);
                    return (
                      <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 2fr) minmax(80px, 1fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(130px, 1.3fr) 60px' }}>
                        <div className="flex items-center"><span className="">{payment.txId}</span></div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                            <img 
                              src={Profile}
                              alt={businessInfo.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="" title={businessInfo.name}>
                            {businessInfo.name}
                          </span>
                        </div>
                        <div className="flex items-center"><span className="">{payment.plan}</span></div>
                        <div className="flex items-center"><span className="">${payment.amount}</span></div>
                        <div className="flex items-center">
                          <AdminStatusBadge status={payment.paymentStatus} label={payment.paymentStatus} />
                        </div>
                        <div className="flex items-center">
                          <AdminStatusBadge status={payment.chargebackStatus} label={payment.chargebackStatus} />
                        </div>
                        <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                          <div className="relative inline-flex" ref={el => paymentRowActionsRefs.current[payment.txId] = el}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPaymentRowActionsOpen((prev) =>
                                  prev[payment.txId] ? {} : { [payment.txId]: true }
                                );
                              }}
                              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                              title="Row Actions"
                            >
                              <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                            </button>
                            {paymentRowActionsOpen[payment.txId] && (
                              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                                <div className="py-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // TODO: Navigate to payment view
                                      setPaymentRowActionsOpen(prev => ({ ...prev, [payment.txId]: false }));
                                    }}
                                    className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                  >
                                    <AdminSupportingText>View Payment</AdminSupportingText>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DashboardMainContainer>

              {/* Quotes & Jobs */}
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Job / Quote-Linked Events
                  </AdminPanelTitle>
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(100px, 1.2fr) minmax(150px, 2fr) minmax(150px, 2fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(100px, 1fr) 60px' }}>
                    <div className="flex items-center"><span className="font-semibold">Quote ID</span></div>
                    <div className="flex items-center"><span className="font-semibold">Customer</span></div>
                    <div className="flex items-center"><span className="font-semibold">Business</span></div>
                    <div className="flex items-center"><span className="font-semibold">Job Status</span></div>
                    <div className="flex items-center"><span className="font-semibold">Chargeback / Complaint</span></div>
                    <div className="flex items-center"><span className="font-semibold">Amount</span></div>
                    <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                  </div>
                  {incident.linkedData.quotes.map((quote, idx) => {
                    const customerInfo = parseAccountName(quote.customer);
                    const businessInfo = parseAccountName(quote.business);
                    return (
                      <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(100px, 1.2fr) minmax(150px, 2fr) minmax(150px, 2fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(100px, 1fr) 60px' }}>
                        <div className="flex items-center"><span className="">{quote.id}</span></div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                            <img 
                              src={Profile}
                              alt={customerInfo.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="" title={customerInfo.name}>
                            {customerInfo.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                            <img 
                              src={Profile}
                              alt={businessInfo.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="" title={businessInfo.name}>
                            {businessInfo.name}
                          </span>
                        </div>
                        <div className="flex items-center"><span className="">{quote.jobStatus}</span></div>
                        <div className="flex items-center"><span className="">{quote.chargebackComplaint}</span></div>
                        <div className="flex items-center"><span className="">${quote.amount.toLocaleString()}</span></div>
                        <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                          <div className="relative inline-flex" ref={el => quoteRowActionsRefs.current[quote.id] = el}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuoteRowActionsOpen((prev) =>
                                  prev[quote.id] ? {} : { [quote.id]: true }
                                );
                              }}
                              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                              title="Row Actions"
                            >
                              <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                            </button>
                            {quoteRowActionsOpen[quote.id] && (
                              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                                <div className="py-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // TODO: Navigate to job view
                                      setQuoteRowActionsOpen(prev => ({ ...prev, [quote.id]: false }));
                                    }}
                                    className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                  >
                                    <AdminSupportingText>View Job</AdminSupportingText>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DashboardMainContainer>

              {/* Moderation Cases & Flags */}
              <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
                <div className="space-y-4 min-w-0 max-w-full">
                  <AdminPanelTitle className="text-offblack pb-2 border-b border-lightgray">
                    Moderation Cases & Flags
                  </AdminPanelTitle>
                  <div
                    className="grid w-full max-w-full min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2"
                    style={{ gridTemplateColumns: INCIDENT_MODERATION_CASES_GRID }}
                  >
                    <div className="flex items-center min-w-0"><AdminTableHeader>ID</AdminTableHeader></div>
                    <div className="flex items-center min-w-0"><AdminTableHeader>Type</AdminTableHeader></div>
                    <div className="flex items-center min-w-0"><AdminTableHeader>Target</AdminTableHeader></div>
                    <div className="flex items-center min-w-0"><AdminTableHeader>Severity</AdminTableHeader></div>
                    <div className="flex items-center min-w-0"><AdminTableHeader>Status</AdminTableHeader></div>
                    <div className="flex items-center justify-center min-w-0"><AdminTableHeader>Actions</AdminTableHeader></div>
                  </div>
                  {paginatedModerationCases.map((modCase) => (
                    <div
                      key={modCase.id}
                      className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                      style={{ gridTemplateColumns: INCIDENT_MODERATION_CASES_GRID }}
                    >
                      <div className="flex items-start min-w-0">
                        <AdminTableBody className="min-w-0 break-words leading-snug">{modCase.id}</AdminTableBody>
                      </div>
                      <div className="flex items-start min-w-0">
                        <AdminTableBody className="min-w-0 break-words leading-snug">{modCase.type}</AdminTableBody>
                      </div>
                      <div className="flex items-start min-w-0">
                        <AdminTableBody className="min-w-0 break-words leading-snug">{modCase.target}</AdminTableBody>
                      </div>
                      <div className="flex items-start min-w-0">
                        <AdminTableBody emphasis className="min-w-0 break-words leading-snug">
                          {modCase.severity}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-start min-w-0">
                        <AdminFlagStatusBadge status={modCase.status} />
                      </div>
                      <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                        <div className="relative inline-flex" ref={el => moderationRowActionsRefs.current[modCase.id] = el}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setModerationRowActionsOpen((prev) =>
                                prev[modCase.id] ? {} : { [modCase.id]: true }
                              );
                            }}
                            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                            title="Row Actions"
                          >
                            <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                          </button>
                          {moderationRowActionsOpen[modCase.id] && (
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                              <div className="py-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // TODO: Navigate to moderation case view
                                    setModerationRowActionsOpen(prev => ({ ...prev, [modCase.id]: false }));
                                  }}
                                  className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                >
                                  <AdminSupportingText>Open Moderation Case</AdminSupportingText>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {incidentModerationCases.length === 0 && (
                    <div className="py-8 text-center">
                      <AdminSupportingText className="text-mediumgray">No moderation cases found</AdminSupportingText>
                    </div>
                  )}
                </div>
                <ConfigurationListPaginationFooter
                  show={moderationCasesPagination.showPagination}
                  rangeStart={moderationCasesPagination.rangeStart}
                  rangeEnd={moderationCasesPagination.rangeEnd}
                  totalCount={incidentModerationCases.length}
                  currentPage={moderationCasesPagination.effectivePage}
                  totalPages={moderationCasesPagination.totalPages}
                  onPrev={() => setModerationCasesPage((prev) => Math.max(1, prev - 1))}
                  onNext={() =>
                    setModerationCasesPage((prev) =>
                      Math.min(moderationCasesPagination.totalPages, prev + 1)
                    )
                  }
                  goToPage={setModerationCasesPage}
                />
              </DashboardMainContainer>
            </div>
          )}

          {/* Impact & Escalation Tab */}
          {activeTab === 'impact' && (
            <div className="space-y-6">
              {/* 3 Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Column 1: Impact Assessment */}
                <DashboardMainContainer className="!bg-white">
                  <div className="space-y-4">
                    <AdminPanelTitle className="text-offblack">
                      Impact Assessment
                    </AdminPanelTitle>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Accounts Affected</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        Customers: {incident.impact.accountsAffected.customers} · Businesses: {incident.impact.accountsAffected.businesses}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Financial Exposure (Estimated)</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        ${incident.impact.financialExposure.toLocaleString()}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Confirmed Loss</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        ${incident.impact.confirmedLoss.toLocaleString()}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Regions Affected</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.impact.regionsAffected.join(', ')}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Core Areas</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.impact.coreAreas.join(', ')}
                      </div>
                    </div>
                  </div>
                </DashboardMainContainer>

                {/* Column 2: Legal / Regulatory */}
                <DashboardMainContainer className="!bg-white">
                  <div className="space-y-4">
                    <AdminPanelTitle className="text-offblack">
                      Legal / Regulatory
                    </AdminPanelTitle>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Legal Team Involved</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.legal.legalTeamInvolved ? 'Yes' : 'No'}
                      </div>
                    </div>
                    {incident.legal.legalTeamInvolved && (
                      <>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Legal Owner</AdminDetailFieldLabel>
                          <div className="text-sm text-offblack font-medium">
                            {incident.legal.legalOwner}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Legal Notes</AdminDetailFieldLabel>
                          <div className="text-sm text-offblack font-medium">
                            {incident.legal.legalNotes}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Regulators / Government Bodies</AdminDetailFieldLabel>
                      <div className="flex flex-wrap gap-2">
                        {incident.legal.regulators.length > 0 ? (
                          incident.legal.regulators.map((regulator, idx) => (
                            <span key={idx} className="px-2 py-1 bg-lightgray text-offblack rounded text-xs">
                              {regulator}
                            </span>
                          ))
                        ) : (
                          <div className="text-sm text-offblack font-medium">None</div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Data Breach / Privacy Impact</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.legal.dataBreachPrivacyImpact}
                      </div>
                    </div>
                  </div>
                </DashboardMainContainer>

                {/* Column 3: Law Enforcement / External */}
                <DashboardMainContainer className="!bg-white">
                  <div className="space-y-4">
                    <AdminPanelTitle className="text-offblack">
                      Law Enforcement / External
                    </AdminPanelTitle>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Law Enforcement Notified</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.lawEnforcement.notified ? 'Yes' : 'No'}
                      </div>
                    </div>
                    {incident.lawEnforcement.notified && (
                      <>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Agency</AdminDetailFieldLabel>
                          <div className="text-sm text-offblack font-medium">
                            {incident.lawEnforcement.agency || '—'}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Reference Number</AdminDetailFieldLabel>
                          <div className="text-sm text-offblack font-medium">
                            {incident.lawEnforcement.referenceNumber || '—'}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Date</AdminDetailFieldLabel>
                          <div className="text-sm text-offblack font-medium">
                            {incident.lawEnforcement.date ? formatDate(incident.lawEnforcement.date) : '—'}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Third-Party Platforms Involved</AdminDetailFieldLabel>
                      <div className="flex flex-wrap gap-2">
                        {incident.lawEnforcement.thirdPartyPlatforms.length > 0 ? (
                          incident.lawEnforcement.thirdPartyPlatforms.map((platform, idx) => (
                            <span key={idx} className="px-2 py-1 bg-lightgray text-offblack rounded text-xs">
                              {platform}
                            </span>
                          ))
                        ) : (
                          <div className="text-sm text-offblack font-medium">None</div>
                        )}
                      </div>
                    </div>
                  </div>
                </DashboardMainContainer>
              </div>
            </div>
          )}

          {/* Actions & Restrictions Tab */}
          {activeTab === 'actions' && (
            <div className="space-y-6">
              {/* Summary Strip */}
              <DashboardMainContainer className="!bg-white">
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Active Restrictions</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.actionsRestrictions.activeRestrictions}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Accounts Fully Suspended</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.actionsRestrictions.accountsFullySuspended}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Permanent Bans</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.actionsRestrictions.permanentBans}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Posting Blocked</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">
                        {incident.actionsRestrictions.postingBlocked} businesses
                      </div>
                    </div>
                  </div>
                </div>
              </DashboardMainContainer>

              {/* Restrictions Overview */}
              <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack">
                    Restrictions Overview
                  </AdminPanelTitle>
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 2fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(150px, 1.5fr) minmax(140px, 1.4fr) minmax(100px, 1fr) 60px' }}>
                    <div className="flex items-center"><span className="font-semibold">Restriction ID</span></div>
                    <div className="flex items-center"><span className="font-semibold">Account</span></div>
                    <div className="flex items-center"><span className="font-semibold">Type</span></div>
                    <div className="flex items-center"><span className="font-semibold">Restriction Type</span></div>
                    <div className="flex items-center"><span className="font-semibold">Reason</span></div>
                    <div className="flex items-center"><span className="font-semibold">Start Date</span></div>
                    <div className="flex items-center"><span className="font-semibold">Status</span></div>
                    <div className="flex items-center justify-center"><span className="font-semibold">Actions</span></div>
                  </div>
                  {incident.actionsRestrictions.restrictions.map((restriction, idx) => {
                    const accountInfo = parseAccountName(restriction.account);
                    return (
                      <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 2fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(150px, 1.5fr) minmax(140px, 1.4fr) minmax(100px, 1fr) 60px' }}>
                        <div className="flex items-center"><span className="">{restriction.id}</span></div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                            <img 
                              src={Profile}
                              alt={accountInfo.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="" title={accountInfo.name}>
                            {accountInfo.name}
                          </span>
                        </div>
                        <div className="flex items-center"><span className="">{restriction.accountType}</span></div>
                        <div className="flex items-center"><span className="">{restriction.restrictionType}</span></div>
                        <div className="flex items-center"><span className="">{restriction.reason}</span></div>
                        <div className="flex items-center"><span className="">{formatDateTime(restriction.startDate)}</span></div>
                        <div className="flex items-center">
                          <AdminStatusBadge status={restriction.status} label={restriction.status} />
                        </div>
                        <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                          <div className="relative inline-flex" ref={el => actionsRestrictionRowActionsRefs.current[restriction.id] = el}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActionsRestrictionRowActionsOpen((prev) =>
                                  prev[restriction.id] ? {} : { [restriction.id]: true }
                                );
                              }}
                              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                              title="Row Actions"
                            >
                              <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                            </button>
                            {actionsRestrictionRowActionsOpen[restriction.id] && (
                              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                                <div className="py-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/admin/fraud/restrictions/${restriction.id}`);
                                      setActionsRestrictionRowActionsOpen(prev => ({ ...prev, [restriction.id]: false }));
                                    }}
                                    className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                                  >
                                    <AdminSupportingText>Open Restriction</AdminSupportingText>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DashboardMainContainer>

              {/* Recommended Next Actions */}
              <DashboardMainContainer className="!bg-white">
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack">
                    Recommended Next Actions
                  </AdminPanelTitle>
                  <ul className="list-disc list-inside space-y-2">
                    {incident.actionsRestrictions.recommendedActions.map((action, idx) => (
                      <li key={idx} className="text-offblack ">
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              </DashboardMainContainer>
            </div>
          )}

          {/* Admin Notes Tab */}
          {activeTab === 'notes' && (
            <div>
              <div className="space-y-4">
                <div className={ADMIN_TOOLBAR_CLASS}>
                  <div className="flex justify-end items-center w-full">
                      <AdminHeaderAction
                      onClick={() => openNotesModal()}
                    >
                      Add Note
                    </AdminHeaderAction>
                  </div>
                </div>
                
                {/* Notes Table */}
                <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                  {/* Header */}
                  <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
                    <div className="flex items-center">
                      <AdminTableHeader>Created At</AdminTableHeader>
                    </div>
                    <div className="flex items-center">
                      <AdminTableHeader>Added By</AdminTableHeader>
                    </div>
                    <div className="flex items-center">
                      <AdminTableHeader>Note</AdminTableHeader></div>
                  </div>
                  
                  {/* Rows - Sort by date (newest first) */}
                  {(() => {
                    const sortedNotes = [...(incident.notes || [])].sort((a, b) => {
                      const aDate = new Date(a.createdAt || a.timestamp || 0);
                      const bDate = new Date(b.createdAt || b.timestamp || 0);
                      return bDate - aDate;
                    });

                    return sortedNotes.length > 0 ? sortedNotes.map((note) => {
                      const noteContent = note.noteContent || note.content || '';
                      const createdAt = note.createdAt ? formatNoteDateTime(note.createdAt) : 
                                       (note.timestamp ? formatNoteDateTime(new Date(note.timestamp)) : 
                                       (note.date ? formatNoteDateTime(new Date(note.date)) : '—'));
                      
                      const addedByDisplay = getAdminNoteAddedByDisplay(note);

                      return (
                        <div key={note.id} className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
                          <div className="flex items-start">
                            <span className="">{createdAt}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="font-medium">{addedByDisplay}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="whitespace-pre-wrap">{noteContent}</span>
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="py-8 text-center">
                        <span className="text-mediumgray">No notes yet</span>
                      </div>
                    );
                  })()}
                </DashboardMainContainer>

                {/* Add Note Modal */}
                {notesModalOpen && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeNotesModal}>
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-between items-center mb-4">
                        <AdminPanelTitle className="text-offblack">
                          Add Note
                        </AdminPanelTitle>
                        <button
                          onClick={closeNotesModal}
                          className="text-offblack hover:text-primaryblue"
                        >
                          <i className="fi fi-rr-cross dashboard-icon"></i>
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Note - Required */}
                        <div>
                          <AdminDetailFieldLabel>
                            Note <span className="text-alertred">*</span>
                          </AdminDetailFieldLabel>
                          <textarea
                            value={noteForm.noteText}
                            onChange={(e) => setNoteForm(prev => ({ ...prev, noteText: e.target.value }))}
                            className={adminFormTextareaClasses()}
                            placeholder="Enter note..."
                            required
                          />
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 pt-4">
                          <AdminFormSecondaryAction type="button" onClick={closeNotesModal}
                            >Cancel</AdminFormSecondaryAction>
                      <AdminHeaderAction
                      onClick={handleNoteSave}
                          >
                            Save Note
                          </AdminHeaderAction>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
            </div>
          </DashboardMainContainer>

        </div>
      </div>
    </AdminLayout>
  );
};

export default SecurityIncidentDetail;

